// features/projects/templates/continuum/findings.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM FINDINGS — Tab 6
//
// Curated visualization layer for the project's research output.
// Three charts, each one defensible claim, each with provenance:
//
//   1. The 7 Dynamics Scorecard — matrix of every dynamic with
//      mean / median / verdict / confidence / methodology version
//   2. Distribution Spread — overlapping eco/human histograms for
//      a selected dynamic, with median + mean markers
//   3. Watch List Trajectories — top-15 systems from Tipping
//      Signature with peak→trough→current shape per row
//
// Designed for researchers first, donors second. Honest about
// limits (sample sizes inline, methodology version stamps,
// provenance footers). Restrained palette — color carries
// meaning not decoration.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';
import { NextMovesPanel } from './finding-next-moves';

// ─── Section structure for the 2-column layout ────────────────
const SECTIONS = [
  { id: 'summary',     number: '01', label: 'Cross-System Summary',      description: 'Frame: where the data on each side comes from, and how that constrains comparisons.' },
  { id: 'scorecard',   number: '02', label: 'Scorecard',                 description: 'The 7 dynamics side by side. The master reference.' },
  { id: 'cross-ref',   number: '03', label: 'Cross-Domain Headline',     description: 'The single chart that summarizes whether the cross-system parallel holds.' },
  { id: 'distribution', number: '04', label: 'Distribution Analysis',    description: 'Per-dynamic distributions and watch-list trajectories.' },
  { id: 'prescription', number: '05', label: 'Prescription Library',     description: 'Interventions tied to surviving findings.' },
  { id: 'provenance',   number: '06', label: 'Provenance & Sources',     description: 'Where every number on this page comes from.' },
];

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const VERDICT_COLOR = {
  PARALLEL:        C.sage,
  'NEAR-PARALLEL': C.gold,
  DIVERGENT:       C.terra,
  INSUFFICIENT:    C.amber,
  insufficient_data: C.amber,
  PENDING:         C.dim,
  pending:         C.dim,
  ERROR:           C.terra,
  error:           C.terra,
};

export default function ContinuumFindings({ workspaceId }) {
  const [run, setRun] = useState(null);
  const [comparisons, setComparisons] = useState(null);
  const [measurementBias, setMeasurementBias] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/comparisons?workspace_id=${workspaceId}`);
    if (r.ok) {
      const j = await r.json();
      setRun(j.run);
      setComparisons(j.comparisons || []);
      setMeasurementBias(j.measurementBias || null);
    } else {
      setComparisons([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const loading = comparisons === null;

  return (
    <FindingsLayout
      run={run}
      loading={loading}
      onReload={reload}
      comparisons={comparisons}
      measurementBias={measurementBias}
      workspaceId={workspaceId}
    />
  );
}

// ─── 2-column layout shell ────────────────────────────────────
// At >=1024px: sticky 220px sidebar (anchors + filters) + scrollable
// main pane. Below: sidebar collapses to a top dropdown so all
// horizontal space goes to content.
function FindingsLayout({ run, loading, onReload, comparisons, measurementBias, workspaceId }) {
  const isWide = useViewportMedia('(min-width: 1024px)');
  const mainRef = useRef(null);
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [filters, setFilters] = useState({ confidence: null, verdict: null });

  // Compute filtered comparisons once; charts that respect filters
  // consume the filtered set, others ignore it.
  const filteredComparisons = useMemo(
    () => applyFilters(comparisons || [], filters),
    [comparisons, filters],
  );

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(`fnd-${id}`);
    if (el && mainRef.current) {
      // scroll within the main pane, not the page
      const top = el.offsetTop - 8;
      mainRef.current.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header run={run} loading={loading} onReload={onReload} />

      {/* Body: 2-column at wide; single-column with top dropdown otherwise */}
      <div style={{
        flex: 1, minHeight: 0,
        display: 'grid',
        gridTemplateColumns: isWide ? '220px 1fr' : '1fr',
      }}>
        {isWide ? (
          <FindingsSidebar
            activeSection={activeSection}
            onJump={scrollToSection}
            filters={filters}
            onFilterChange={setFilters}
            comparisons={comparisons || []}
          />
        ) : (
          <FindingsTopNav
            activeSection={activeSection}
            onJump={scrollToSection}
            filters={filters}
            onFilterChange={setFilters}
            comparisons={comparisons || []}
          />
        )}

        <div
          ref={mainRef}
          style={{
            overflow: 'auto',
            padding: '18px 22px 60px 22px',
          }}
        >
          {loading ? (
            <Empty msg="Loading findings…" />
          ) : !run ? (
            <Empty msg="No simulation run yet. Run a simulation first." />
          ) : (
            <>
              <FindingsSection
                section={SECTIONS[0]}
                onActiveChange={setActiveSection}
                scrollContainer={mainRef}
              >
                <MeasurementBiasCallout bias={measurementBias} />
              </FindingsSection>

              <FindingsSection
                section={SECTIONS[1]}
                onActiveChange={setActiveSection}
                scrollContainer={mainRef}
              >
                <ScorecardChart
                  run={run}
                  comparisons={filteredComparisons}
                  totalCount={comparisons?.length || 0}
                  workspaceId={workspaceId}
                  hasActiveFilters={!!(filters.confidence || filters.verdict)}
                />
              </FindingsSection>

              <FindingsSection
                section={SECTIONS[2]}
                onActiveChange={setActiveSection}
                scrollContainer={mainRef}
              >
                <CrossReferenceMap run={run} comparisons={comparisons} />
              </FindingsSection>

              <FindingsSection
                section={SECTIONS[3]}
                onActiveChange={setActiveSection}
                scrollContainer={mainRef}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(540px, 1fr))',
                  gap: 18,
                }}>
                  <DistributionChart run={run} comparisons={comparisons} />
                  <TrajectoriesChart run={run} comparisons={comparisons} />
                </div>
              </FindingsSection>

              <FindingsSection
                section={SECTIONS[4]}
                onActiveChange={setActiveSection}
                scrollContainer={mainRef}
              >
                <PrescriptionLibrary comparisons={comparisons} />
              </FindingsSection>

              <FindingsSection
                section={SECTIONS[5]}
                onActiveChange={setActiveSection}
                scrollContainer={mainRef}
              >
                <Provenance run={run} comparisons={comparisons} />
              </FindingsSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar (≥1024px) — sticky anchors + filter chips ───────
function FindingsSidebar({ activeSection, onJump, filters, onFilterChange, comparisons }) {
  return (
    <div style={{
      borderRight: `1px solid ${C.b1}`,
      background: C.s1,
      overflow: 'auto',
      padding: '20px 16px',
      display: 'flex', flexDirection: 'column', gap: 22,
    }}>
      <SectionAnchorList activeSection={activeSection} onJump={onJump} />
      <FilterChips filters={filters} onChange={onFilterChange} comparisons={comparisons} />
    </div>
  );
}

// ─── Top nav fallback (<1024px) — anchors as a dropdown ──────
function FindingsTopNav({ activeSection, onJump, filters, onFilterChange, comparisons }) {
  return (
    <div style={{
      borderBottom: `1px solid ${C.b1}`,
      background: C.s1,
      padding: '10px 22px',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <select
        value={activeSection}
        onChange={(e) => onJump(e.target.value)}
        style={{
          background: 'transparent', border: `1px solid ${C.b2}`, borderRadius: 4,
          color: C.text, fontFamily: F.mono, fontSize: 11, padding: '4px 10px',
          letterSpacing: '0.04em', cursor: 'pointer', outline: 'none',
        }}
      >
        {SECTIONS.map(s => (
          <option key={s.id} value={s.id}>{s.number} · {s.label}</option>
        ))}
      </select>
      <FilterChips filters={filters} onChange={onFilterChange} comparisons={comparisons} compact />
    </div>
  );
}

function SectionAnchorList({ activeSection, onJump }) {
  return (
    <div>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.dim, textTransform: 'uppercase', marginBottom: 10,
        fontWeight: 600,
      }}>
        ◆ Sections
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {SECTIONS.map(s => {
          const isActive = s.id === activeSection;
          return (
            <button
              key={s.id}
              onClick={() => onJump(s.id)}
              style={{
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                padding: '6px 8px 6px 10px',
                cursor: 'pointer',
                borderLeft: `2px solid ${isActive ? C.gold : 'transparent'}`,
                color: isActive ? C.gold : C.static,
                fontFamily: F.mono, fontSize: 10, letterSpacing: '0.04em',
                lineHeight: 1.5,
                transition: 'color 0.12s, border-color 0.12s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = C.mid; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = C.static; }}
            >
              <div style={{ fontSize: 8, color: C.dim, letterSpacing: '0.12em', marginBottom: 1 }}>
                {s.number}
              </div>
              <div>{s.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterChips({ filters, onChange, comparisons, compact = false }) {
  // Three confidence tiers + three verdict classes that exist in the data.
  // Toggle behavior: click sets the filter; click again clears.
  const setConfidence = (v) => onChange({ ...filters, confidence: filters.confidence === v ? null : v });
  const setVerdict    = (v) => onChange({ ...filters, verdict:    filters.verdict    === v ? null : v });

  const counts = useMemo(() => {
    const out = { conf: {}, verd: {} };
    for (const c of comparisons) {
      const cf = (c.confidence || '').toUpperCase();
      const vd = (c.verdict || '').toUpperCase();
      if (cf) out.conf[cf] = (out.conf[cf] || 0) + 1;
      if (vd) out.verd[vd] = (out.verd[vd] || 0) + 1;
    }
    return out;
  }, [comparisons]);

  const Section = ({ title, children }) => compact ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.12em',
        color: C.dim, textTransform: 'uppercase',
      }}>{title}</span>
      <div style={{ display: 'flex', gap: 4 }}>{children}</div>
    </div>
  ) : (
    <div>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.dim, textTransform: 'uppercase', marginBottom: 8,
        fontWeight: 600,
      }}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', gap: compact ? 14 : 16, flexWrap: 'wrap' }}>
      <Section title="Confidence">
        {['HIGH', 'MEDIUM', 'LOW'].map(v => (
          <Chip key={v} label={v} count={counts.conf[v] || 0}
                active={filters.confidence === v}
                onClick={() => setConfidence(v)}
                color={v === 'HIGH' ? C.sage : v === 'MEDIUM' ? C.gold : C.dim} />
        ))}
      </Section>
      <Section title="Verdict">
        {['PARALLEL', 'NEAR-PARALLEL', 'DIVERGENT'].map(v => (
          <Chip key={v} label={v} count={counts.verd[v] || 0}
                active={filters.verdict === v}
                onClick={() => setVerdict(v)}
                color={VERDICT_COLOR[v] || C.dim} />
        ))}
      </Section>
    </div>
  );
}

function Chip({ label, count, active, onClick, color }) {
  const dim = count === 0 && !active;
  return (
    <button
      onClick={onClick}
      disabled={count === 0 && !active}
      style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.06em',
        textTransform: 'uppercase', fontWeight: 600,
        padding: '3px 8px', borderRadius: 3,
        border: `1px solid ${active ? color : (dim ? C.b1 : C.b2)}`,
        background: active ? `${color}20` : 'transparent',
        color: active ? color : (dim ? C.dim : C.mid),
        cursor: dim ? 'not-allowed' : 'pointer',
        transition: 'all 0.12s',
      }}
    >
      {label} <span style={{ opacity: 0.7, marginLeft: 3 }}>{count}</span>
    </button>
  );
}

// ─── Section wrapper with scroll-spy ──────────────────────────
function FindingsSection({ section, onActiveChange, scrollContainer, children }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const root = scrollContainer?.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) onActiveChange(section.id);
        }
      },
      { root, rootMargin: '-20% 0px -65% 0px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [section.id, onActiveChange, scrollContainer]);

  return (
    <section
      id={`fnd-${section.id}`}
      ref={ref}
      style={{ marginBottom: 36, scrollMarginTop: 8 }}
    >
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 14,
        marginBottom: 14, paddingBottom: 8,
        borderBottom: `1px solid ${C.b1}`,
      }}>
        <span style={{
          fontFamily: F.mono, fontSize: 9, color: C.dim, letterSpacing: '0.18em',
          fontWeight: 600,
        }}>
          {section.number}
        </span>
        <span style={{
          fontFamily: F.serif, fontSize: 22, fontWeight: 300, color: C.text,
        }}>
          {section.label}
        </span>
        <span style={{
          marginLeft: 'auto', fontFamily: F.sans, fontSize: 11, color: C.dim,
          fontStyle: 'italic', textAlign: 'right', maxWidth: 360, lineHeight: 1.5,
        }}>
          {section.description}
        </span>
      </div>
      {children}
    </section>
  );
}

// ─── Filter application ───────────────────────────────────────
function applyFilters(comparisons, filters) {
  if (!filters.confidence && !filters.verdict) return comparisons;
  return comparisons.filter(c => {
    if (filters.confidence && (c.confidence || '').toUpperCase() !== filters.confidence) return false;
    if (filters.verdict    && (c.verdict    || '').toUpperCase() !== filters.verdict)    return false;
    return true;
  });
}

// ─── Viewport media-query hook ────────────────────────────────
function useViewportMedia(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

// ─── Header strip ──────────────────────────────────────────
function Header({ run, loading, onReload }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', padding:'0 20px', height:42,
      borderBottom:`1px solid ${C.b1}`, background:C.s1, flexShrink:0, gap:14,
    }}>
      <div style={{
        display:'flex', alignItems:'center', gap:6,
        fontSize:9, padding:'3px 10px', borderRadius:4,
        background:'rgba(155,143,196,.10)', border:'1px solid rgba(155,143,196,.25)',
        color:C.purple || C.amber,
      }}>
        <div style={{ width:5, height:5, borderRadius:'50%', background:C.purple || C.amber }} />
        Findings — Research Visualizations
      </div>
      {run && (
        <div style={{ fontSize:9, color:C.dim }}>
          Based on Run {run.id?.slice(0,8)} · {run.completed_at ? new Date(run.completed_at).toLocaleString(undefined, { month:'short', day:'numeric', year:'numeric' }) : ''}
        </div>
      )}
      <div style={{ marginLeft:'auto' }}>
        <button
          onClick={onReload}
          disabled={loading}
          style={{
            fontSize:9, padding:'5px 14px', borderRadius:4,
            background:'transparent', border:`1px solid ${C.b2}`,
            color:C.static, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily:F.mono, letterSpacing:'0.06em',
          }}
        >
          {loading ? '⟳ Loading' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div style={{ height:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:C.dim, fontSize:12, fontStyle:'italic' }}>{msg}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CHART 1 — THE 7 DYNAMICS SCORECARD
// ═══════════════════════════════════════════════════════════
function ScorecardChart({ run, comparisons, workspaceId, totalCount, hasActiveFilters }) {
  // Render each known dynamic — even if it has no comparison row
  // (pending dynamics shown dimmed, honest about scope). When the
  // sidebar filter is active, only dynamics whose comparison row
  // matches the filter render; pending dynamics are still shown
  // (they have no verdict to filter against, and hiding them
  // would silently misrepresent project scope).
  const byKey = Object.fromEntries((comparisons || []).map(c => [c.dynamic_key, c]));
  const rows = DYNAMICS
    .map(d => ({ dyn: d, comp: byKey[d.key] || null }))
    .filter(r => {
      if (!hasActiveFilters) return true;
      // Pending dynamics: only show when no filters active
      // (otherwise filter by verdict would never match).
      return !!r.comp;
    });

  const filterFootnote = hasActiveFilters
    ? `Showing ${rows.length} of ${DYNAMICS.length} dynamics (filtered) · ${comparisons.length} of ${totalCount || comparisons.length} comparison rows`
    : null;

  return (
    <ChartFrame
      title="The 7 Dynamics — Scorecard"
      subtitle={`Cross-system comparisons as of run ${run.id?.slice(0,8)}`}
      footnote={filterFootnote || `v${run.methodology?.mvp_threshold?.version || '—'} et al · methodology versions stored per row · click to expand`}
      reasoning={{
        shows:   'Every dynamic the project tests, side-by-side: ecological mean (with sample n) vs human mean, the gap (Δ), the statistical significance (p), the effect size (Cohen\'s d), the verdict, and the confidence level. Pending dynamics are dimmed but listed honestly.',
        read:    'Each row is one cross-system comparison. DIVERGENT means the eco and human distributions are statistically different at the population level; PARALLEL means indistinguishable. Confidence (HIGH/MEDIUM/LOW) reflects sample size — a DIVERGENT verdict at LOW confidence is suggestive but not yet defensible.',
        matters: 'This is the project\'s report card. Anyone — researcher, donor, reviewer — can scan in 30 seconds what we\'ve tested, what we\'ve found, what we haven\'t done, and what version of the methodology produced each finding.',
      }}
    >
      <div style={{
        background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6, overflow:'hidden',
      }}>
        {/* Sticky column header — stays at top of viewport while
            scrolling through the scorecard rows. Releases when the
            scorecard section is scrolled past. */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 2,
          display:'grid', gridTemplateColumns:'1.5fr 90px 1fr 1fr 60px 60px 80px 70px',
          padding:'9px 16px', borderBottom:`1px solid ${C.b1}`,
          fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em',
          textTransform:'uppercase', color:C.dim, gap:8, alignItems:'baseline',
          background: C.s1,
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}>
          <div>Dynamic / gaps · D M C E</div>
          <div>Solution</div>
          <div>Eco mean (n) · med</div>
          <div>Human mean (n) · med</div>
          <div style={{ textAlign:'right' }}>Δ</div>
          <div style={{ textAlign:'right' }}>p / d</div>
          <div style={{ textAlign:'right' }}>Verdict</div>
          <div style={{ textAlign:'right' }}>Conf.</div>
        </div>
        {rows.length === 0 ? (
          <div style={{
            padding: '40px 20px', textAlign: 'center',
            fontFamily: F.sans, fontSize: 12, color: C.dim, fontStyle: 'italic',
          }}>
            No dynamics match the current filter. Clear filters in the sidebar to see the full scorecard.
          </div>
        ) : (
          rows.map((r, i) => <ScorecardRow key={r.dyn.key} row={r} last={i === rows.length - 1} workspaceId={workspaceId} />)
        )}
      </div>
    </ChartFrame>
  );
}

// ── Gap severity → color (data/measurement/causation/ethics) ──
const GAP_COLOR = {
  low:       C.sage,
  medium:    C.gold,
  high:      C.amber,
  very_high: C.terra,
};

// ── Solution status → color + label ───────────────────────
const SOLUTION_META = {
  prescriptive:    { color: C.sage,   label: 'PRESCRIPTIVE' },
  predictive_only: { color: C.gold,   label: 'PREDICTIVE'   },
  not_ready:       { color: C.dim,    label: 'NOT READY'    },
};

// ── Tiny gap pill — single letter, color-coded by severity ─
function GapPill({ letter, severity, title }) {
  const color = GAP_COLOR[severity] || C.dim;
  return (
    <span
      title={title}
      style={{
        display:'inline-flex', alignItems:'center', justifyContent:'center',
        width:14, height:14, borderRadius:3,
        background:`${color}25`, border:`1px solid ${color}60`,
        color, fontSize:8, fontFamily:F.mono, fontWeight:500,
        letterSpacing:0,
      }}
    >
      {letter}
    </span>
  );
}

function ScorecardRow({ row, last, workspaceId }) {
  const [expanded, setExpanded] = useState(false);
  const { dyn, comp } = row;
  const isPending = !comp || comp.verdict === 'pending' || comp.verdict === 'error';
  const verdict = comp?.verdict || 'PENDING';
  const verdictColor = VERDICT_COLOR[verdict] || C.dim;

  const ecoMean   = comp?.eco_mean;
  const humanMean = comp?.human_mean;
  const ecoMed    = comp?.data_inputs?.eco?.median;
  const humanMed  = comp?.data_inputs?.human?.median;
  const delta     = (ecoMean != null && humanMean != null) ? Math.abs(ecoMean - humanMean) : null;

  const gaps = dyn.gaps || {};
  const solution = SOLUTION_META[dyn.solution_status] || SOLUTION_META.not_ready;

  return (
    <>
      <div
        onClick={() => comp && setExpanded(v => !v)}
        style={{
          display:'grid', gridTemplateColumns:'1.5fr 90px 1fr 1fr 60px 60px 80px 70px',
          padding:'10px 16px', gap:8,
          borderBottom: !last || expanded ? `1px solid ${C.b3 || C.b2}` : 'none',
          alignItems:'baseline', fontSize:11,
          opacity: isPending ? 0.5 : 1,
          cursor: comp ? 'pointer' : 'default',
          transition:'background 0.12s',
          background: expanded ? C.s2 : 'transparent',
        }}
      >
        <div>
          <div style={{ fontFamily:F.serif, fontSize:13, color:C.text, fontWeight:400 }}>
            {dyn.label}
          </div>
          <div style={{
            display:'flex', alignItems:'center', gap:5, marginTop:4,
          }}>
            <span style={{ fontFamily:F.mono, fontSize:8, color:C.dim, letterSpacing:'0.06em' }}>
              v{comp?.methodology_version || dyn.version}
            </span>
            <span style={{ display:'inline-flex', gap:3, marginLeft:4 }}>
              <GapPill letter="D" severity={gaps.data}        title={`Data gap: ${gaps.data || '—'}`} />
              <GapPill letter="M" severity={gaps.measurement} title={`Measurement gap: ${gaps.measurement || '—'}`} />
              <GapPill letter="C" severity={gaps.causation}   title={`Causation gap: ${gaps.causation || '—'}`} />
              <GapPill letter="E" severity={gaps.ethics}      title={`Ethics gap: ${gaps.ethics || '—'}`} />
            </span>
          </div>
        </div>
        <div>
          <span style={{
            display:'inline-block', fontSize:8, padding:'2px 7px', borderRadius:3,
            background:`${solution.color}15`, border:`1px solid ${solution.color}50`,
            color:solution.color, letterSpacing:'0.08em', fontFamily:F.mono, fontWeight:500,
            whiteSpace:'nowrap',
          }}>
            {solution.label}
          </span>
        </div>
        <div style={{ fontFamily:F.mono, fontSize:11, color:C.sage }}>
          {ecoMean != null ? Number(ecoMean).toFixed(3) : '—'}
          <span style={{ color:C.dim, fontSize:10 }}> ({comp?.eco_n ?? 0})</span>
          {ecoMed != null && (
            <div style={{ fontSize:9, color:C.dim, marginTop:1 }}>med {Number(ecoMed).toFixed(3)}</div>
          )}
        </div>
        <div style={{ fontFamily:F.mono, fontSize:11, color:C.terra }}>
          {humanMean != null ? Number(humanMean).toFixed(3) : '—'}
          <span style={{ color:C.dim, fontSize:10 }}> ({comp?.human_n ?? 0})</span>
          {humanMed != null && (
            <div style={{ fontSize:9, color:C.dim, marginTop:1 }}>med {Number(humanMed).toFixed(3)}</div>
          )}
        </div>
        <div style={{ fontFamily:F.mono, fontSize:11, color:C.gold, textAlign:'right' }}>
          {delta != null ? delta.toFixed(3) : '—'}
        </div>
        <div style={{ fontFamily:F.mono, fontSize:10, color:C.mid, textAlign:'right' }}>
          {comp?.p_value != null ? Number(comp.p_value).toFixed(4) : '—'}
          <div style={{ fontSize:9, color:C.dim, marginTop:1 }}>
            d={comp?.effect_size != null ? Number(comp.effect_size).toFixed(2) : '—'}
          </div>
        </div>
        <div style={{ textAlign:'right' }}>
          <span style={{
            display:'inline-block', fontSize:8, padding:'2px 7px', borderRadius:3,
            background:`${verdictColor}15`, border:`1px solid ${verdictColor}50`,
            color:verdictColor, letterSpacing:'0.06em', fontFamily:F.mono,
          }}>
            {verdict === 'insufficient_data' ? 'INSUF' : verdict.toUpperCase()}
          </span>
        </div>
        <div style={{ textAlign:'right', fontFamily:F.mono, fontSize:10, color:C.static }}>
          {comp?.confidence || '—'}
        </div>
      </div>
      {expanded && comp && (
        <div style={{
          padding:'14px 18px',
          borderBottom: !last ? `1px solid ${C.b3 || C.b2}` : 'none',
          background:C.s2,
          display:'flex', flexDirection:'column', gap:12,
        }}>
          {/* Plain-English finding */}
          <div>
            <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', color:C.gold, marginBottom:6, textTransform:'uppercase' }}>
              Plain-English finding
            </div>
            <div style={{ fontSize:11, color:C.mid, lineHeight:1.6, fontFamily:F.sans }}>
              {comp.finding_text || '(no finding text)'}
            </div>
          </div>

          {/* Ecological prescription — the nature-validated solution */}
          {dyn.ecological_prescription && (
            <div style={{
              padding:'10px 12px',
              background:'rgba(122,158,130,.06)', border:'1px solid rgba(122,158,130,.30)',
              borderRadius:5,
            }}>
              <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', color:C.sage, marginBottom:6, textTransform:'uppercase', fontWeight:500 }}>
                ◆ Ecological Prescription
              </div>
              <div style={{ fontSize:11, color:C.mid, lineHeight:1.65, fontFamily:F.sans }}>
                {dyn.ecological_prescription}
              </div>
            </div>
          )}

          {/* Human translation */}
          {dyn.human_translation && (
            <div style={{
              padding:'10px 12px',
              background:'rgba(196,80,80,.05)', border:'1px solid rgba(196,80,80,.25)',
              borderRadius:5,
            }}>
              <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', color:C.terra, marginBottom:6, textTransform:'uppercase', fontWeight:500 }}>
                ◆ Human Translation
              </div>
              <div style={{ fontSize:11, color:C.mid, lineHeight:1.65, fontFamily:F.sans }}>
                {dyn.human_translation}
              </div>
            </div>
          )}

          {/* What to do next — actionable moves derived from finding state */}
          <NextMovesPanel comp={comp} dyn={dyn} workspaceId={workspaceId} />

          {/* Gap analysis detail */}
          {dyn.gaps && (
            <div style={{
              display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8,
              padding:'10px 12px',
              background:C.bg, border:`1px solid ${C.b1}`, borderRadius:5,
            }}>
              {[
                { key: 'data',        label: 'Data',        desc: 'Is the data needed available?' },
                { key: 'measurement', label: 'Measurement', desc: 'Is there agreed methodology to measure this?' },
                { key: 'causation',   label: 'Causation',   desc: 'Is the mechanism connecting eco→human established?' },
                { key: 'ethics',      label: 'Ethics',      desc: 'Are there ethical questions about applying this?' },
              ].map(g => (
                <div key={g.key}>
                  <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:3 }}>
                    <GapPill letter={g.label[0]} severity={dyn.gaps[g.key]} title={g.desc} />
                    <span style={{ fontFamily:F.mono, fontSize:8, color:C.dim, letterSpacing:'0.1em', textTransform:'uppercase' }}>
                      {g.label}
                    </span>
                  </div>
                  <div style={{ fontSize:9, color:GAP_COLOR[dyn.gaps[g.key]] || C.dim, fontFamily:F.mono, letterSpacing:'0.04em', textTransform:'uppercase' }}>
                    {dyn.gaps[g.key] || 'unknown'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Methodology footer */}
          <div style={{ fontFamily:F.mono, fontSize:9, color:C.dim, lineHeight:1.6 }}>
            extractor_id: {dyn.extractor_id || '—'}<br/>
            sources: eco={(dyn.eco_sources || []).join(',') || '—'} · human={(dyn.human_sources || []).join(',') || '—'}<br/>
            min_n_per_side: {dyn.min_n_per_side} · cluster: {dyn.cluster || '—'}
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// CROSS-REFERENCE MAP — Engine B's BOTH-domain behaviors
// rendered as eco↔human pairs of specific events.
//
// The cross-references between mother nature and human systems
// already exist in our data — they're the behaviors Claude tagged
// as transferability='both' during Engine B Phase 1. This chart
// makes the connection visible by pairing real eco events with
// real human events that exhibit the same behavior shape.
//
// Headline-finding chart. Researcher-facing. Citation-ready.
// ═══════════════════════════════════════════════════════════

// Source-key heuristic: which side does this event come from?
// Conservative — if we can't classify, we exclude rather than guess.
const ECO_SOURCES   = new Set(['lpi', 'gbif', 'ebird', 'iucn']);
const HUMAN_SOURCES = new Set(['owid', 'fdic', 'glottolog', 'imf', 'census']);

function isEco(e)   { return ECO_SOURCES.has((e.source_key || '').toLowerCase()); }
function isHuman(e) { return HUMAN_SOURCES.has((e.source_key || '').toLowerCase()); }

function CrossReferenceMap({ run }) {
  const behaviors = run?.behaviors?.behaviors || [];
  const bothBehaviors = behaviors.filter(b => b.transferability === 'both');
  const svgRef = useRef(null);

  if (!run?.behaviors) {
    return (
      <ChartFrame
        title="Cross-Reference Map"
        subtitle="Behaviors crossing mother nature and human systems"
      >
        <div style={{
          padding:'18px 22px', background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6,
          fontSize:11, color:C.dim, lineHeight:1.6, fontStyle:'italic',
        }}>
          No Engine B behaviors discovered yet for this run. Open the Simulation tab → Behaviors (Open) → ▶ Discover Behaviors to populate this view.
        </div>
      </ChartFrame>
    );
  }

  if (bothBehaviors.length === 0) {
    return (
      <ChartFrame
        title="Cross-Reference Map"
        subtitle="Behaviors crossing mother nature and human systems"
      >
        <div style={{
          padding:'18px 22px', background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6,
          fontSize:11, color:C.mid, lineHeight:1.6,
        }}>
          Engine B discovered {behaviors.length} behavior{behaviors.length === 1 ? '' : 's'} for this run, but none were tagged <span style={{ color:C.sage, fontFamily:F.mono }}>transferability=both</span>. The cross-reference signal isn&apos;t present in this dataset — try re-running discovery, expanding sample size, or wiring more cross-domain data sources.
        </div>
      </ChartFrame>
    );
  }

  return (
    <ChartFrame
      title="Cross-Reference Map"
      subtitle="Behaviors that cross mother nature and human systems"
      footnote={`Pairs surfaced from Engine B Phase 1 behaviors with transferability="both". Run ${run.id?.slice(0,8)} · model ${run.behaviors_model || '—'}.`}
      onExportPng={() => downloadSvgAsPng(svgRef.current, `continuum_cross_reference_map_${run.id?.slice(0,6)}.png`)}
      reasoning={{
        shows:   'Behavior patterns that Engine B (hypothesis-free Claude analysis) identified as crossing both ecological AND human systems. Each section is one behavior; within each section, real eco events are paired with real human events that exhibit the same trajectory shape.',
        read:    'Compare the trajectory lines side-by-side. When the shapes match — fast crash followed by stuck-near-zero, or U-shaped recovery to a plateau — that\'s a cross-domain dynamic. The mechanism hypothesis at the top of each section names the underlying force the data implies (carrying capacity, structural ceiling, etc.).',
        matters: 'This is the project\'s thesis made visible. If Hartebeest behaves like Tajikistan, ecology and economics share a dynamic that transfers between domains. The shapes carry the argument without prose. This is what\'s citable in a paper.',
      }}
    >
      <div ref={svgRef} style={{
        background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6,
        padding:'16px 18px',
      }}>
        {/* Header strip — what the chart is showing */}
        <div style={{
          display:'flex', alignItems:'center', gap:14, marginBottom:14,
          paddingBottom:12, borderBottom:`1px solid ${C.b2}`,
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:9, letterSpacing:'0.16em', textTransform:'uppercase',
            color:C.sage, fontWeight:500,
          }}>
            ECO ←→ HUMAN
          </div>
          <div style={{ fontSize:10, color:C.dim }}>
            {bothBehaviors.length} behaviour{bothBehaviors.length === 1 ? '' : 's'} crossing both domains
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:14, fontSize:9, color:C.dim, fontFamily:F.mono }}>
            <div><span style={{ color:C.sage }}>■</span> ecological event</div>
            <div><span style={{ color:C.terra }}>■</span> human event</div>
            <div><span style={{ color:C.gold }}>↔</span> shared pattern</div>
          </div>
        </div>

        {/* One section per BOTH behavior */}
        <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
          {bothBehaviors.map((b, i) => <BehaviorSection key={i} behavior={b} num={i + 1} />)}
        </div>
      </div>
    </ChartFrame>
  );
}

function BehaviorSection({ behavior, num }) {
  const events = behavior.evidence_events || [];
  const ecoEvents   = events.filter(isEco);
  const humanEvents = events.filter(isHuman);
  const pairs = pairUpEvents(ecoEvents, humanEvents);
  const confColor = behavior.confidence === 'strong' ? C.sage
    : behavior.confidence === 'recurring' ? C.gold
    : C.amber;

  return (
    <div style={{
      background:C.s2, border:`1px solid ${C.b2}`, borderRadius:6,
      overflow:'hidden',
    }}>
      {/* Behavior header */}
      <div style={{
        padding:'12px 16px', borderBottom:`1px solid ${C.b1}`,
        background:'rgba(122,158,130,.04)',
      }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:6 }}>
          <div style={{ fontFamily:F.mono, fontSize:9, color:C.dim }}>
            {String(num).padStart(2, '0')}
          </div>
          <div style={{ fontFamily:F.serif, fontSize:16, fontWeight:300, color:C.text, flex:1 }}>
            {behavior.name || '(unnamed behavior)'}
          </div>
          <div style={{
            fontSize:8, padding:'2px 7px', borderRadius:3,
            background:`${confColor}15`, border:`1px solid ${confColor}50`,
            color:confColor, letterSpacing:'0.08em', fontFamily:F.mono,
          }}>
            {(behavior.confidence || '').toUpperCase()}
          </div>
        </div>
        {behavior.mechanism_hypothesis && (
          <div style={{ fontSize:11, color:C.mid, lineHeight:1.55, fontFamily:F.sans }}>
            {behavior.mechanism_hypothesis}
          </div>
        )}
      </div>

      {/* Event pairs */}
      {pairs.length === 0 ? (
        <div style={{ padding:'14px 16px', fontSize:11, color:C.dim, fontStyle:'italic' }}>
          No matched eco↔human pairs in this behavior&apos;s evidence (need at least one event from each side).
        </div>
      ) : (
        <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>
          {pairs.map((p, i) => <PairCard key={i} pair={p} />)}
        </div>
      )}
    </div>
  );
}

// Pair sequentially after sorting by drawdown_pct desc (deepest crashes
// first), so the most extreme cases on each side line up. Falls back to
// value if drawdown_pct missing. Caps at 5 pairs per behavior to keep
// the chart scannable.
function pairUpEvents(eco, human) {
  const sortFn = (a, b) => {
    const ad = a.drawdown_pct, bd = b.drawdown_pct;
    if (ad != null && bd != null) return bd - ad;
    return (b.value ?? 0) - (a.value ?? 0);
  };
  const ecoSorted   = [...eco].sort(sortFn);
  const humanSorted = [...human].sort(sortFn);
  const n = Math.min(ecoSorted.length, humanSorted.length, 5);
  const pairs = [];
  for (let i = 0; i < n; i++) {
    pairs.push({ eco: ecoSorted[i], human: humanSorted[i] });
  }
  return pairs;
}

function PairCard({ pair }) {
  return (
    <div style={{
      display:'grid', gridTemplateColumns:'1fr 36px 1fr', gap:8,
      alignItems:'stretch',
    }}>
      <EventMiniCard event={pair.eco}   side="eco" />
      <Connector />
      <EventMiniCard event={pair.human} side="human" />
    </div>
  );
}

function EventMiniCard({ event, side }) {
  const accent     = side === 'eco' ? C.sage : C.terra;
  const accentTint = side === 'eco' ? 'rgba(122,158,130,.06)' : 'rgba(196,80,80,.06)';
  const peak    = event.peak_value;
  const trough  = event.trough_value;
  const current = event.current_value;
  const ratio   = event.value;
  const drawdown = event.drawdown_pct;

  return (
    <div style={{
      background:accentTint, border:`1px solid ${accent}40`, borderRadius:5,
      padding:'10px 12px',
    }}>
      {/* Name + source */}
      <div style={{
        fontFamily:F.serif, fontSize:13, color:C.text, fontWeight:400, marginBottom:4,
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
      }}>
        {event.system_name || '(unnamed)'}
      </div>
      <div style={{
        fontFamily:F.mono, fontSize:8, color:accent, letterSpacing:'0.1em', marginBottom:8,
      }}>
        {(event.source_key || '?').toUpperCase()} · {side.toUpperCase()}
      </div>

      {/* Trajectory: peak → trough → current (or peak → current when
          trough is missing, e.g. for Tipping Signature events) */}
      <PairTrajectory peak={peak} trough={trough} current={current}
        peakYear={event.peak_year} troughYear={event.trough_year} currentYear={event.current_year}
        color={accent}
      />

      {/* Stats row */}
      <div style={{
        display:'flex', flexWrap:'wrap', gap:10, marginTop:8,
        fontFamily:F.mono, fontSize:10, color:C.mid,
      }}>
        {ratio != null && (
          <div>ratio <span style={{ color:accent, fontWeight:500 }}>{Number(ratio).toFixed(3)}</span></div>
        )}
        {drawdown != null && (
          <div>drawdown <span style={{ color:C.gold }}>{(Number(drawdown) * 100).toFixed(0)}%</span></div>
        )}
      </div>
    </div>
  );
}

// Inline trajectory: peak → trough → current as a small SVG.
// Year labels live in a dedicated row below the trajectory — no
// overlap with the line. Falls back to a 2-point line when trough
// is missing (e.g. Tipping Signature events that only carry peak +
// current). Renders nothing if fewer than 2 valid points.
function PairTrajectory({ peak, trough, current, peakYear, troughYear, currentYear, color }) {
  const W = 200;
  const TRAJ_H = 38;          // top portion for the line
  const LABEL_H = 14;         // bottom portion for year labels
  const H = TRAJ_H + LABEL_H;
  const PAD_X = 10;           // horizontal padding inside the SVG

  // Build points conditionally — skip any missing values.
  const raw = [];
  if (peak    != null && Number.isFinite(Number(peak)))    raw.push({ val: Number(peak),    yr: peakYear,    label: 'peak'    });
  if (trough  != null && Number.isFinite(Number(trough)))  raw.push({ val: Number(trough),  yr: troughYear,  label: 'trough'  });
  if (current != null && Number.isFinite(Number(current))) raw.push({ val: Number(current), yr: currentYear, label: 'current' });

  if (raw.length < 2) {
    // Fallback: render a flat placeholder bar so card heights stay aligned
    return (
      <div style={{
        height: H, display:'flex', alignItems:'center', justifyContent:'center',
        fontSize: 9, color: C.dim, fontStyle: 'italic',
      }}>
        no trajectory data
      </div>
    );
  }

  const max = Math.max(...raw.map(p => p.val));
  const min = Math.min(...raw.map(p => p.val));
  const range = max - min || 1;
  const yAt = (v) => 6 + ((max - v) / range) * (TRAJ_H - 12);

  // Distribute x positions evenly across the available width
  const innerW = W - PAD_X * 2;
  const xStep = raw.length === 1 ? 0 : innerW / (raw.length - 1);
  const pts = raw.map((p, i) => ({
    ...p,
    x: PAD_X + i * xStep,
    y: yAt(p.val),
  }));

  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
         style={{ display:'block' }}>
      {/* Baseline at the bottom of the trajectory area (above the label row) */}
      <line x1="0" y1={TRAJ_H - 1} x2={W} y2={TRAJ_H - 1} stroke={C.b2} strokeOpacity="0.4" />
      {/* Trajectory line */}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeOpacity="0.85" />
      {/* Point markers */}
      {pts.map((p, i) => (
        <circle key={`pt${i}`} cx={p.x} cy={p.y} r="2.5" fill={color}>
          <title>{`${p.label} ${p.val}${p.yr ? ` (${p.yr})` : ''}`}</title>
        </circle>
      ))}
      {/* Year labels in their own row below the trajectory — never overlap */}
      {pts.map((p, i) => p.yr ? (
        <text key={`yr${i}`} x={p.x} y={TRAJ_H + 10}
              textAnchor={i === 0 ? 'start' : i === pts.length - 1 ? 'end' : 'middle'}
              fontFamily="DM Mono, monospace" fontSize="8" fill={C.dim}>
          {p.yr}
        </text>
      ) : null)}
    </svg>
  );
}

function Connector() {
  return (
    <div style={{
      display:'flex', alignItems:'center', justifyContent:'center',
      flexDirection:'column', gap:4,
    }}>
      <div style={{
        fontSize:14, color:C.gold, fontFamily:F.mono, fontWeight:500,
      }}>
        ↔
      </div>
      <div style={{
        fontSize:7, color:C.dim, fontFamily:F.mono, letterSpacing:'0.14em',
        writingMode:'horizontal-tb', textAlign:'center',
      }}>
        SAME<br/>SHAPE
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CHART 2 — DISTRIBUTION SPREAD
// Uses histogram.bins already computed during the simulation
// run. Selectable dynamic via dropdown. SVG-rendered so PNG
// export works via the SVG-to-canvas helper below.
// ═══════════════════════════════════════════════════════════
function DistributionChart({ run, comparisons }) {
  // Default to the dynamic with the strongest signal — DIVERGENT first,
  // then NEAR-PARALLEL, then anything with both sides populated.
  const usable = (comparisons || []).filter(c => {
    const eb = c.data_inputs?.eco?.histogram?.bins;
    const hb = c.data_inputs?.human?.histogram?.bins;
    return Array.isArray(eb) && Array.isArray(hb) && eb.some(v => v > 0) && hb.some(v => v > 0);
  });
  const ranked = [...usable].sort((a, b) => priorityOf(b) - priorityOf(a));
  const defaultKey = ranked[0]?.dynamic_key;
  const [selectedKey, setSelectedKey] = useState(defaultKey);

  // Re-default if comparisons change
  useEffect(() => {
    if (defaultKey && !selectedKey) setSelectedKey(defaultKey);
  }, [defaultKey, selectedKey]);

  const comp = (comparisons || []).find(c => c.dynamic_key === selectedKey);
  const dyn  = DYN_BY_KEY[selectedKey];

  const svgRef = useRef(null);

  if (!comp || !dyn) {
    return (
      <ChartFrame title="Distribution Spread" subtitle="Overlapping eco vs human distributions">
        <div style={{ padding:24, fontSize:11, color:C.dim, fontStyle:'italic' }}>
          No usable distributions in this run.
        </div>
      </ChartFrame>
    );
  }

  return (
    <ChartFrame
      title={`Distribution Spread — ${dyn.label}`}
      subtitle={`Eco vs human · v${comp.methodology_version || dyn.version}`}
      footnote={`Bins from data_inputs.histogram (computed during run ${run.id?.slice(0,8)}). Vertical lines = medians.`}
      reasoning={{
        shows:   'The full distribution of values for both domains, overlaid. Each bar is a count of events in a value range. Vertical dashed lines mark the medians; circles below the axis mark the means. The verdict pill in the corner shows the t-test result.',
        read:    'Compare the SHAPES, not just the centers. Overlapping distributions (eco bars sitting under human bars) means the two domains exhibit the same range of behavior. Wider distributions on one side mean that domain has more heterogeneity. When median and mean diverge, the mean is being pulled by outliers in a long tail.',
        matters: 'A single mean can hide everything. The Tipping Signature finding (means similar, eco distribution 1.5× wider) is invisible from the Scorecard alone — you need the distributions to see it. Heavy-tail-honest analysis (median + IQR alongside mean + std) is how the project avoids over-claiming.',
      }}
      controls={
        <select
          value={selectedKey || ''}
          onChange={(e) => setSelectedKey(e.target.value)}
          style={{
            background:C.s2, color:C.text, border:`1px solid ${C.b2}`, borderRadius:4,
            padding:'4px 8px', fontFamily:F.mono, fontSize:10, letterSpacing:'0.06em',
          }}
        >
          {usable.map(c => {
            const d = DYN_BY_KEY[c.dynamic_key];
            return <option key={c.dynamic_key} value={c.dynamic_key}>{d?.label || c.dynamic_key}</option>;
          })}
        </select>
      }
      onExportPng={() => downloadSvgAsPng(svgRef.current, `continuum_${selectedKey}_distribution_${run.id?.slice(0,6)}.png`)}
    >
      <DistributionSvg
        svgRef={svgRef}
        dyn={dyn}
        comp={comp}
      />
      <DistributionInsights comp={comp} dyn={dyn} />
    </ChartFrame>
  );
}

// SVG-rendered overlapping distribution. Pure react, no chart lib.
function DistributionSvg({ dyn, comp, svgRef }) {
    const ecoBins   = comp.data_inputs?.eco?.histogram?.bins   || [];
    const humanBins = comp.data_inputs?.human?.histogram?.bins || [];
    const histMin = comp.data_inputs?.eco?.histogram?.min ?? 0;
    const histMax = comp.data_inputs?.eco?.histogram?.max ?? 1;
    const ecoMean   = comp.eco_mean;
    const humanMean = comp.human_mean;
    const ecoMed    = comp.data_inputs?.eco?.median;
    const humanMed  = comp.data_inputs?.human?.median;

    const W = 540, H = 280;
    const M = { top: 28, right: 18, bottom: 38, left: 42 };
    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;

    const maxCount = Math.max(...ecoBins, ...humanBins, 1);
    const nBins = Math.max(ecoBins.length, humanBins.length, 1);
    const barW = innerW / nBins;

    const xValueAt = (v) => M.left + ((v - histMin) / (histMax - histMin || 1)) * innerW;
    const yCount = (n) => M.top + innerH - (n / maxCount) * innerH;

    return (
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
           style={{ display:'block', background:C.bg }}>
        {/* axis baseline */}
        <line x1={M.left} y1={M.top + innerH} x2={M.left + innerW} y2={M.top + innerH} stroke={C.b1} strokeWidth="1" />
        <line x1={M.left} y1={M.top} x2={M.left} y2={M.top + innerH} stroke={C.b1} strokeWidth="1" />

        {/* x ticks */}
        {[0, 0.25, 0.5, 0.75, 1].map((p) => {
          const x = M.left + p * innerW;
          const v = histMin + p * (histMax - histMin);
          return (
            <g key={p}>
              <line x1={x} y1={M.top + innerH} x2={x} y2={M.top + innerH + 4} stroke={C.b2} />
              <text x={x} y={M.top + innerH + 16} textAnchor="middle"
                    fontFamily="DM Mono, monospace" fontSize="9" fill={C.dim}>
                {Number(v).toFixed(2)}
              </text>
            </g>
          );
        })}

        {/* x label */}
        <text x={M.left + innerW / 2} y={H - 6} textAnchor="middle"
              fontFamily="DM Mono, monospace" fontSize="9" fill={C.dim} letterSpacing="0.12em">
          {(dyn.units || 'value').toUpperCase()}
        </text>

        {/* y ticks */}
        {[0, 0.25, 0.5, 0.75, 1].map(p => {
          const y = M.top + innerH - p * innerH;
          const c = Math.round(p * maxCount);
          return (
            <g key={p}>
              <line x1={M.left - 4} y1={y} x2={M.left} y2={y} stroke={C.b2} />
              <text x={M.left - 8} y={y + 3} textAnchor="end"
                    fontFamily="DM Mono, monospace" fontSize="9" fill={C.dim}>{c}</text>
            </g>
          );
        })}

        {/* eco bars (sage, translucent) */}
        {ecoBins.map((n, i) => {
          if (n === 0) return null;
          const x = M.left + i * barW;
          const y = yCount(n);
          const h = M.top + innerH - y;
          return (
            <rect key={`e${i}`} x={x + 1} y={y} width={Math.max(barW - 2, 1)} height={h}
                  fill={C.sage} fillOpacity="0.45" />
          );
        })}
        {/* human bars (terra, translucent) */}
        {humanBins.map((n, i) => {
          if (n === 0) return null;
          const x = M.left + i * barW;
          const y = yCount(n);
          const h = M.top + innerH - y;
          return (
            <rect key={`h${i}`} x={x + 1} y={y} width={Math.max(barW - 2, 1)} height={h}
                  fill={C.terra} fillOpacity="0.45" />
          );
        })}

        {/* median markers */}
        {ecoMed != null && (
          <g>
            <line x1={xValueAt(ecoMed)} y1={M.top - 4} x2={xValueAt(ecoMed)} y2={M.top + innerH}
                  stroke={C.sage} strokeWidth="1.5" strokeDasharray="3 3" />
            <text x={xValueAt(ecoMed)} y={M.top - 8} textAnchor="middle"
                  fontFamily="DM Mono, monospace" fontSize="9" fill={C.sage}>
              eco med {Number(ecoMed).toFixed(2)}
            </text>
          </g>
        )}
        {humanMed != null && (
          <g>
            <line x1={xValueAt(humanMed)} y1={M.top - 4} x2={xValueAt(humanMed)} y2={M.top + innerH}
                  stroke={C.terra} strokeWidth="1.5" strokeDasharray="3 3" />
            <text x={xValueAt(humanMed)} y={M.top - 18} textAnchor="middle"
                  fontFamily="DM Mono, monospace" fontSize="9" fill={C.terra}>
              human med {Number(humanMed).toFixed(2)}
            </text>
          </g>
        )}

        {/* mean markers */}
        {ecoMean != null && (
          <circle cx={xValueAt(ecoMean)} cy={M.top + innerH + 22} r="3" fill={C.sage} />
        )}
        {humanMean != null && (
          <circle cx={xValueAt(humanMean)} cy={M.top + innerH + 22} r="3" fill={C.terra} />
        )}

        {/* legend (top-right) */}
        <g transform={`translate(${W - 130}, 8)`}>
          <rect x="0" y="0" width="9" height="9" fill={C.sage} fillOpacity="0.45" />
          <text x="13" y="8" fontFamily="DM Mono, monospace" fontSize="9" fill={C.sage}>
            eco (n={comp.eco_n || 0})
          </text>
          <rect x="0" y="13" width="9" height="9" fill={C.terra} fillOpacity="0.45" />
          <text x="13" y="21" fontFamily="DM Mono, monospace" fontSize="9" fill={C.terra}>
            human (n={comp.human_n || 0})
          </text>
        </g>
      </svg>
    );
}

function DistributionInsights({ comp, dyn }) {
  const ecoMean   = comp.eco_mean;
  const humanMean = comp.human_mean;
  const ecoStd    = comp.eco_std;
  const humanStd  = comp.human_std;
  const p = comp.p_value;
  const d = comp.effect_size;
  const verdict = comp.verdict;
  const verdictColor = VERDICT_COLOR[verdict] || C.dim;

  return (
    <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:8 }}>
      <div style={{
        display:'flex', alignItems:'baseline', gap:14,
        padding:'10px 14px', background:C.s2, border:`1px solid ${C.b2}`, borderRadius:5,
        fontFamily:F.mono, fontSize:11,
      }}>
        <div>p={p != null ? Number(p).toFixed(4) : '—'}</div>
        <div>d={d != null ? Number(d).toFixed(3) : '—'}</div>
        <div style={{ color:C.sage }}>eco σ={ecoStd != null ? Number(ecoStd).toFixed(3) : '—'}</div>
        <div style={{ color:C.terra }}>human σ={humanStd != null ? Number(humanStd).toFixed(3) : '—'}</div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{
            fontSize:9, padding:'2px 7px', borderRadius:3,
            background:`${verdictColor}15`, border:`1px solid ${verdictColor}50`,
            color:verdictColor, letterSpacing:'0.06em',
          }}>{verdict}</span>
          <span style={{ fontSize:9, color:C.static }}>{comp.confidence || '—'}</span>
        </div>
      </div>
      <div style={{ fontFamily:F.sans, fontSize:11, color:C.mid, lineHeight:1.6, padding:'0 4px' }}>
        {distributionInsightSentence({ ecoMean, humanMean, ecoStd, humanStd, verdict, dyn })}
      </div>
    </div>
  );
}

function distributionInsightSentence({ ecoMean, humanMean, ecoStd, humanStd, verdict, dyn }) {
  if (ecoMean == null || humanMean == null) return 'Insufficient data on one or both sides.';
  const stdRatio = (ecoStd != null && humanStd != null && humanStd > 0) ? ecoStd / humanStd : null;
  const meansClose = Math.abs(ecoMean - humanMean) < 0.1;
  if (verdict === 'PARALLEL' || (meansClose && stdRatio != null && Math.abs(stdRatio - 1) < 0.3)) {
    return `Both domains center near similar ${dyn.short || 'values'} (${ecoMean.toFixed(2)} vs ${humanMean.toFixed(2)}) with comparable spreads. The dynamic appears to operate similarly across system types.`;
  }
  if (meansClose && stdRatio != null && stdRatio > 1.4) {
    return `Means are similar (${ecoMean.toFixed(2)} vs ${humanMean.toFixed(2)}) but eco distribution is ${stdRatio.toFixed(1)}× wider. The headline asymmetry is in the spread, not the central tendency.`;
  }
  if (verdict === 'DIVERGENT') {
    return `Distributions sit at meaningfully different centers (eco ${ecoMean.toFixed(2)} vs human ${humanMean.toFixed(2)}). Even controlling for spread, the gap is statistically robust.`;
  }
  return `Eco ${ecoMean.toFixed(2)} (σ=${ecoStd?.toFixed(2) || '—'}) vs human ${humanMean.toFixed(2)} (σ=${humanStd?.toFixed(2) || '—'}). Read the verdict + confidence for the formal call.`;
}

function priorityOf(c) {
  let s = 0;
  const v = c.verdict, conf = c.confidence;
  if (v === 'DIVERGENT')          s += 4;
  else if (v === 'NEAR-PARALLEL') s += 3;
  else if (v === 'PARALLEL')      s += 2;
  if (conf === 'HIGH')   s += 3;
  else if (conf === 'MEDIUM') s += 2;
  else if (conf === 'LOW')    s += 1;
  return s;
}

// ═══════════════════════════════════════════════════════════
// CHART 3 — WATCH LIST TRAJECTORIES
// Top 15 systems by Tipping Signature score with peak→trough
// →current trajectory shape per row. Inline SVG sparklines.
// ═══════════════════════════════════════════════════════════
function TrajectoriesChart({ run, comparisons }) {
  const tipping = (comparisons || []).find(c => c.dynamic_key === 'tipping_signature');
  const svgRef = useRef(null);

  if (!tipping?.data_inputs) {
    return (
      <ChartFrame title="Watch List Trajectories" subtitle="Top systems by Tipping Signature">
        <div style={{ padding:24, fontSize:11, color:C.dim, fontStyle:'italic' }}>
          Tipping Signature dynamic hasn&apos;t produced events yet for this run.
        </div>
      </ChartFrame>
    );
  }

  // Build top-15 by score, with side flag
  const eco   = (tipping.data_inputs.eco?.events   || []).map(e => ({ ...e, _side: 'eco' }));
  const human = (tipping.data_inputs.human?.events || []).map(e => ({ ...e, _side: 'human' }));

  // Cross-reference each event with its peak/trough/current from
  // the same run's MVP Threshold OR Recovery Asymmetry events. The
  // Tipping events themselves don't carry trajectory shape, but the
  // other dynamics' events for the same system do.
  const allOtherEvents = [];
  for (const c of comparisons) {
    if (c.dynamic_key === 'tipping_signature') continue;
    allOtherEvents.push(...(c.data_inputs?.eco?.events   || []));
    allOtherEvents.push(...(c.data_inputs?.human?.events || []));
  }
  const trajByName = {};
  for (const e of allOtherEvents) {
    const key = (e.system_name || '').toLowerCase();
    if (!key) continue;
    if (!trajByName[key] && e.peak_value != null && e.trough_value != null && e.current_value != null) {
      trajByName[key] = {
        peak:    Number(e.peak_value),
        trough:  Number(e.trough_value),
        current: Number(e.current_value),
        peakYr:  e.peak_year,
        troughYr: e.trough_year,
        currentYr: e.current_year,
      };
    }
  }

  const top = [...eco, ...human]
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    .slice(0, 15)
    .map(e => ({ ...e, traj: trajByName[(e.system_name || '').toLowerCase()] || null }));

  return (
    <ChartFrame
      title="Watch List Trajectories"
      subtitle="Top 15 systems by Tipping Signature · CSD-based early warning"
      footnote="Sparkline shape = peak → trough → current (from MVP/Recovery dynamic events for the same system, where available)."
      onExportPng={() => downloadSvgAsPng(svgRef.current, `continuum_watch_list_${run.id?.slice(0,6)}.png`)}
      reasoning={{
        shows:   'The 15 systems (eco + human) currently scoring highest on the Tipping-Point Signature dynamic. Score is a composite of lag-1 autocorrelation (50%), variance trend (30%), and coefficient of variation (20%) computed on each system\'s recent time-series sample.',
        read:    'Higher score = stronger Critical Slowing Down signature = system is exhibiting the early-warning signs of approaching a tipping point in the literature (Scheffer 2009, Dakos 2012). Red ≥ 0.8 is critical; amber ≥ 0.6 warrants investigation. Sparkline shape shows the system\'s peak → trough → current trajectory for additional context.',
        matters: 'This is the actionable list — where attention and resources should go right now. Cross-validation with IUCN Red List status (when added as overlay) tests whether CSD methodology agrees with established conservation classifications. The presence of Syria, Liberia, and known endangered species suggests it does.',
      }}
    >
      <svg ref={svgRef} viewBox="0 0 540 600" width="100%" preserveAspectRatio="xMidYMid meet"
           style={{ display:'block', background:C.bg }}>
        {/* column headers */}
        <g fontFamily="DM Mono, monospace" fontSize="9" fill={C.dim} letterSpacing="0.12em">
          <text x="14" y="20">RANK</text>
          <text x="48" y="20">SYSTEM</text>
          <text x="270" y="20">TRAJECTORY</text>
          <text x="395" y="20" textAnchor="end">SCORE</text>
          <text x="450" y="20" textAnchor="end">AC1</text>
          <text x="525" y="20" textAnchor="end">DOMAIN</text>
        </g>
        <line x1="14" y1="28" x2="525" y2="28" stroke={C.b2} />
        {top.map((e, i) => <TrajectoryRow key={i} idx={i} e={e} />)}
      </svg>
      <div style={{
        marginTop:10, fontSize:10, color:C.dim, lineHeight:1.5, fontStyle:'italic',
      }}>
        Higher score = closer to tipping. Score 0.6+ warrants investigation; 0.8+ is critical.
        Sparkline rises = recovering past trough; sparkline still falling = active decline.
      </div>
    </ChartFrame>
  );
}

function TrajectoryRow({ idx, e }) {
  const y = 50 + idx * 35;
  const sysLabel = e.system_name || '(unnamed)';
  const sysShort = sysLabel.length > 30 ? sysLabel.slice(0, 28) + '…' : sysLabel;
  const score = e.value;
  const scoreColor = score >= 0.8 ? C.terra
    : score >= 0.6 ? C.amber
    : score >= 0.4 ? C.gold
    : C.sage;
  const sideColor = e._side === 'eco' ? C.sage : C.terra;

  return (
    <g>
      <text x="14" y={y} fontFamily="DM Mono, monospace" fontSize="10" fill={C.dim}>
        {String(idx + 1).padStart(2, '0')}
      </text>
      <text x="48" y={y} fontFamily="DM Sans, sans-serif" fontSize="11" fill={C.text}>
        {sysShort}
      </text>
      {e.traj && <Sparkline x={270} y={y - 12} traj={e.traj} side={e._side} />}
      <text x="395" y={y} fontFamily="DM Mono, monospace" fontSize="11" fill={scoreColor} fontWeight="500" textAnchor="end">
        {score != null ? Number(score).toFixed(3) : '—'}
      </text>
      <text x="450" y={y} fontFamily="DM Mono, monospace" fontSize="10" fill={C.mid} textAnchor="end">
        {e.lag1_autocorrelation != null ? Number(e.lag1_autocorrelation).toFixed(3) : '—'}
      </text>
      <text x="525" y={y} fontFamily="DM Mono, monospace" fontSize="9" fill={sideColor} letterSpacing="0.08em" textAnchor="end">
        {e._side?.toUpperCase()}
      </text>
      <line x1="14" y1={y + 10} x2="525" y2={y + 10} stroke={C.b3 || C.b2} strokeOpacity="0.5" />
    </g>
  );
}

// 3-point sparkline: peak → trough → current. Width 110px, height 24px.
function Sparkline({ x, y, traj, side }) {
  const W = 110, H = 24;
  const peak = traj.peak;
  const trough = traj.trough;
  const current = traj.current;
  const max = Math.max(peak, trough, current);
  const min = Math.min(peak, trough, current);
  const range = max - min || 1;
  const yAt = (v) => H - ((v - min) / range) * H;
  const stroke = side === 'eco' ? C.sage : C.terra;

  // Order points by year if available — peak first, trough middle,
  // current last is the typical pattern.
  const pts = [
    { x: 0,        y: yAt(peak),    label: 'peak',    val: peak },
    { x: W * 0.5,  y: yAt(trough),  label: 'trough',  val: trough },
    { x: W,        y: yAt(current), label: 'current', val: current },
  ];

  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* baseline */}
      <line x1="0" y1={H} x2={W} y2={H} stroke={C.b2} strokeOpacity="0.4" />
      {/* trajectory line */}
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeOpacity="0.9" />
      {/* point markers */}
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="2" fill={stroke}>
          <title>{`${p.label}: ${p.val}`}</title>
        </circle>
      ))}
    </g>
  );
}

// ═══════════════════════════════════════════════════════════
// PRESCRIPTION LIBRARY — the project's product output
//
// Companion to the Scorecard. Where the Scorecard says "here are
// the verdicts", the Library says "here are the biological
// prescriptions ready to apply, grouped by cluster."
//
// Each card is structured for deck export — self-contained, with
// the ecological mechanism + human translation + current evidence
// status + gap analysis. PNG-export-ready (button placeholder for
// now; full export in a follow-up).
// ═══════════════════════════════════════════════════════════

const CLUSTER_LABELS = {
  organizational_survival: 'Organizational Survival',
  population_demographics: 'Population & Demographics',
  environmental_resource:  'Environmental & Resource',
  economic_systems:        'Economic Systems',
  cultural_social:         'Cultural & Social',
  health_epidemics:        'Health & Epidemics',
  most_ambitious:          'Most Ambitious',
};

const CLUSTER_ORDER = [
  'organizational_survival',
  'population_demographics',
  'environmental_resource',
  'economic_systems',
  'cultural_social',
  'health_epidemics',
  'most_ambitious',
];

function PrescriptionLibrary({ comparisons }) {
  // Filter to prescriptive dynamics from the registry
  const prescriptiveDynamics = DYNAMICS.filter(d => d.solution_status === 'prescriptive');
  if (prescriptiveDynamics.length === 0) return null;

  const compByKey = Object.fromEntries((comparisons || []).map(c => [c.dynamic_key, c]));

  // Group by cluster, preserving CLUSTER_ORDER
  const byCluster = {};
  for (const d of prescriptiveDynamics) {
    const c = d.cluster || 'organizational_survival';
    (byCluster[c] ||= []).push(d);
  }
  const orderedClusters = CLUSTER_ORDER.filter(c => byCluster[c]);

  return (
    <ChartFrame
      title="Prescription Library"
      subtitle="Biological solutions ready to apply to human systems"
      footnote={`${prescriptiveDynamics.length} prescriptive findings from the dynamics registry · grouped by cluster · PNG export per card coming in a future build`}
      reasoning={{
        shows:   `Each card is one ecological prescription paired with its human translation. ${prescriptiveDynamics.length} of the project's currently-defined dynamics carry prescriptive solutions — biological interventions that have been validated by natural selection over millions of years and that can plausibly transfer to human institutions.`,
        read:    'Each card stands alone — researcher / donor / policymaker should be able to read one card without context and understand the prescription, the biological evidence, the human translation, and the current evidence status. Gaps (D/M/C/E pills) show where the cross-domain claim is still open.',
        matters: 'This is the project\'s product output. Findings (verdicts) are research output; prescriptions are commercial output. "Nature already figured this out" is the value proposition that makes The Continuum more than another paper about organizational resilience.',
      }}
    >
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {orderedClusters.map(clusterId => (
          <PrescriptionCluster
            key={clusterId}
            clusterId={clusterId}
            dynamics={byCluster[clusterId]}
            compByKey={compByKey}
          />
        ))}
      </div>
    </ChartFrame>
  );
}

function PrescriptionCluster({ clusterId, dynamics, compByKey }) {
  const label = CLUSTER_LABELS[clusterId] || clusterId;
  return (
    <div>
      <div style={{
        fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em',
        textTransform:'uppercase', color:C.amber, fontWeight:500,
        marginBottom:10, paddingBottom:6, borderBottom:`1px solid ${C.b2}`,
      }}>
        ◆ {label} · {dynamics.length} prescription{dynamics.length === 1 ? '' : 's'}
      </div>
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(auto-fit, minmax(540px, 1fr))',
        gap:12,
      }}>
        {dynamics.map(d => (
          <PrescriptionCard
            key={d.id || d.key}
            dyn={d}
            comp={compByKey[d.id || d.key]}
          />
        ))}
      </div>
    </div>
  );
}

function PrescriptionCard({ dyn, comp }) {
  const verdict = comp?.verdict || (dyn.status === 'pending' ? 'PENDING' : null);
  const verdictColor = VERDICT_COLOR[verdict] || C.dim;
  const conf = comp?.confidence;
  const gaps = dyn.gaps || {};

  // Cluster accent
  const clusterAccent = dyn.cluster === 'environmental_resource' ? C.teal
    : dyn.cluster === 'economic_systems' ? C.gold
    : dyn.cluster === 'cultural_social' ? C.purple || C.amber
    : C.amber;

  return (
    <div style={{
      background:C.s1, border:`1px solid ${C.b2}`, borderRadius:8, overflow:'hidden',
      display:'flex', flexDirection:'column',
    }}>
      {/* Top stripe */}
      <div style={{ height:3, background:clusterAccent }} />

      {/* Header strip — short name + verdict + confidence */}
      <div style={{
        display:'flex', alignItems:'center', gap:10,
        padding:'10px 14px',
        background:`${clusterAccent}10`, borderBottom:`1px solid ${C.b1}`,
      }}>
        <div style={{
          fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
          color:clusterAccent, fontWeight:500, flex:1,
        }}>
          {dyn.short || dyn.label}
        </div>
        {verdict && (
          <span style={{
            fontSize:8, padding:'2px 7px', borderRadius:3,
            background:`${verdictColor}15`, border:`1px solid ${verdictColor}50`,
            color:verdictColor, letterSpacing:'0.06em', fontFamily:F.mono,
          }}>
            {verdict === 'insufficient_data' ? 'INSUF' : verdict.toUpperCase()}
          </span>
        )}
        {conf && (
          <span style={{
            fontSize:8, color:C.static, fontFamily:F.mono, letterSpacing:'0.06em',
          }}>
            {conf}
          </span>
        )}
      </div>

      {/* Title */}
      <div style={{ padding:'14px 18px 6px' }}>
        <div style={{
          fontFamily:F.serif, fontSize:18, fontWeight:300, color:C.text,
          lineHeight:1.3, letterSpacing:'-0.005em',
        }}>
          {dyn.label}
        </div>
      </div>

      {/* Two stacked boxes — ecological prescription + human translation */}
      <div style={{ padding:'8px 18px', display:'flex', flexDirection:'column', gap:10 }}>
        {dyn.ecological_prescription && (
          <div style={{
            padding:'10px 14px',
            background:'rgba(122,158,130,.05)', border:'1px solid rgba(122,158,130,.30)',
            borderRadius:6,
          }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
              color:C.sage, fontWeight:500, marginBottom:6,
            }}>
              ◆ Nature&apos;s Solution
            </div>
            <div style={{
              fontFamily:F.sans, fontSize:11.5, color:C.text, lineHeight:1.6,
            }}>
              {dyn.ecological_prescription}
            </div>
          </div>
        )}

        {dyn.human_translation && (
          <div style={{
            padding:'10px 14px',
            background:'rgba(196,80,80,.04)', border:'1px solid rgba(196,80,80,.25)',
            borderRadius:6,
          }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
              color:C.terra, fontWeight:500, marginBottom:6,
            }}>
              ◆ Human Translation
            </div>
            <div style={{
              fontFamily:F.sans, fontSize:11.5, color:C.text, lineHeight:1.6,
            }}>
              {dyn.human_translation}
            </div>
          </div>
        )}
      </div>

      {/* Footer — gap pills + version */}
      <div style={{
        marginTop:'auto',
        padding:'10px 18px',
        borderTop:`1px solid ${C.b1}`,
        background:'rgba(85,85,102,.04)',
        display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
      }}>
        <div style={{
          fontFamily:F.mono, fontSize:8, color:C.dim, letterSpacing:'0.10em',
          textTransform:'uppercase',
        }}>
          Gaps:
        </div>
        <span style={{ display:'inline-flex', gap:3 }}>
          <GapPill letter="D" severity={gaps.data}        title={`Data gap: ${gaps.data || '—'}`} />
          <GapPill letter="M" severity={gaps.measurement} title={`Measurement gap: ${gaps.measurement || '—'}`} />
          <GapPill letter="C" severity={gaps.causation}   title={`Causation gap: ${gaps.causation || '—'}`} />
          <GapPill letter="E" severity={gaps.ethics}      title={`Ethics gap: ${gaps.ethics || '—'}`} />
        </span>
        <div style={{
          marginLeft:'auto', fontFamily:F.mono, fontSize:8, color:C.dim,
          letterSpacing:'0.06em',
        }}>
          v{dyn.version}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SHARED CHART FRAME
// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════
// MEASUREMENT-BIAS CALLOUT — meta-finding above the scorecard
//
// Surfaces a structural asymmetry the project itself observed:
// the eco-side data we have is mostly studied populations
// (skewed to loss); the human-side data is mostly indicators
// of progress (skewed to gain). The DIVERGENT verdicts further
// down are partly real and partly an artefact of WHAT each
// domain measures. Call that out honestly so readers don't
// over-read the gap.
// ═══════════════════════════════════════════════════════════
function MeasurementBiasCallout({ bias }) {
  if (!bias?.eco?.n || !bias?.human?.n) return null;

  const eco = bias.eco, human = bias.human;
  const ecoLossPct   = eco.n   ? (eco.loss   / eco.n)   * 100 : 0;
  const ecoGainPct   = eco.n   ? (eco.gain   / eco.n)   * 100 : 0;
  const humanLossPct = human.n ? (human.loss / human.n) * 100 : 0;
  const humanGainPct = human.n ? (human.gain / human.n) * 100 : 0;
  // The bias score is the gap between what the two domains chose to measure.
  const biasGap = Math.abs(humanGainPct - ecoGainPct);

  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ marginBottom:12 }}>
        <div style={{
          fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase',
          color:C.purple || C.amber, marginBottom:4, fontWeight:500,
        }}>
          ◆ Meta-Finding — Measurement Bias
        </div>
        <div style={{ fontFamily:F.serif, fontSize:18, fontWeight:300, color:C.text, lineHeight:1.3 }}>
          What each domain chooses to measure shapes what we can compare
        </div>
      </div>

      <div style={{
        background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6,
        padding:'18px 20px',
      }}>
        {/* Two-column shape comparison */}
        <div style={{
          display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:18,
        }}>
          <BiasColumn
            label="Ecological side"
            n={eco.n}
            lossPct={ecoLossPct}
            gainPct={ecoGainPct}
            note="LPI tracks studied populations — biased toward those already at risk."
          />
          <BiasColumn
            label="Human side"
            n={human.n}
            lossPct={humanLossPct}
            gainPct={humanGainPct}
            note="WB / OWID / FRED report development indicators — biased toward what we built up."
          />
        </div>

        {/* Bias score + interpretation */}
        <div style={{
          padding:'14px 16px', background:C.s2, border:`1px solid ${C.b2}`, borderRadius:6,
          fontSize:11, color:C.mid, lineHeight:1.65, fontFamily:F.sans,
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase',
            color:C.gold, marginBottom:8, fontWeight:500,
          }}>
            ◆ Read this before the scorecard below
          </div>
          The two domains we&apos;re comparing don&apos;t carry the same shape of data.
          The eco side is <strong style={{ color:C.text }}>{ecoGainPct.toFixed(0)}% recovery / {ecoLossPct.toFixed(0)}% loss</strong>;
          the human side is <strong style={{ color:C.text }}>{humanGainPct.toFixed(0)}% recovery / {humanLossPct.toFixed(0)}% loss</strong>.
          That&apos;s a <strong style={{ color:C.gold }}>{biasGap.toFixed(0)}-point gap</strong> in what the two fields chose to track.
          When a dynamic below shows DIVERGENT, part of the divergence is the real difference between ecological
          and human collapse-and-recovery; part of it is that ecological monitoring catches loss while
          human-system monitoring catches progress. Both are findings, but they&apos;re different findings — don&apos;t
          read the gap as if both sides were sampled the same way.
        </div>
      </div>

      <div style={{ marginTop:8, fontSize:10, color:C.dim, fontStyle:'italic', lineHeight:1.5 }}>
        Computed live from continuum_eco_events / continuum_human_events. Counts include only rows with peak/trough/current time series (asymmetry-eligible).
      </div>
    </div>
  );
}

function BiasColumn({ label, n, lossPct, gainPct, note }) {
  return (
    <div>
      <div style={{
        fontFamily:F.mono, fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase',
        color:C.dim, marginBottom:6, fontWeight:500,
      }}>
        {label} <span style={{ color:C.mid }}>· n={n.toLocaleString()}</span>
      </div>
      {/* Stacked bar: loss (terra) | gain (sage) */}
      <div style={{
        display:'flex', height:24, borderRadius:4, overflow:'hidden',
        border:`1px solid ${C.b2}`, marginBottom:6,
      }}>
        <div style={{
          width:`${lossPct}%`, background:C.terra, opacity:0.7,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, color:'#fff', fontFamily:F.mono, fontWeight:500,
        }}>
          {lossPct >= 12 ? `${lossPct.toFixed(0)}%` : ''}
        </div>
        <div style={{
          width:`${gainPct}%`, background:C.sage, opacity:0.7,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, color:'#fff', fontFamily:F.mono, fontWeight:500,
        }}>
          {gainPct >= 12 ? `${gainPct.toFixed(0)}%` : ''}
        </div>
      </div>
      <div style={{ display:'flex', gap:14, fontSize:9, color:C.dim, fontFamily:F.mono, marginBottom:8 }}>
        <div><span style={{ color:C.terra }}>■</span> loss</div>
        <div><span style={{ color:C.sage }}>■</span> recovery</div>
      </div>
      <div style={{ fontSize:11, color:C.mid, lineHeight:1.55, fontFamily:F.sans, fontStyle:'italic' }}>
        {note}
      </div>
    </div>
  );
}

function ChartFrame({ title, subtitle, footnote, controls, onExportPng, reasoning, children }) {
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{
        display:'flex', alignItems:'flex-end', gap:14, marginBottom:12, flexWrap:'wrap',
      }}>
        <div style={{ flex:1, minWidth:280 }}>
          <div style={{
            fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase',
            color:C.amber, marginBottom:4, fontWeight:500,
          }}>
            ◆ {title}
          </div>
          <div style={{ fontFamily:F.serif, fontSize:18, fontWeight:300, color:C.text, lineHeight:1.3 }}>
            {subtitle}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {controls}
          {onExportPng && (
            <button
              onClick={onExportPng}
              title="Download this chart as PNG"
              style={{
                fontSize:9, padding:'5px 12px', borderRadius:4,
                background:'transparent', border:`1px solid ${C.b2}`,
                color:C.static, cursor:'pointer',
                fontFamily:F.mono, letterSpacing:'0.06em',
                transition:'color 0.12s, border-color 0.12s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = C.gold; e.currentTarget.style.borderColor = C.gold; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = C.static; e.currentTarget.style.borderColor = C.b2; }}
            >
              ↓ PNG
            </button>
          )}
        </div>
      </div>
      <div>{children}</div>
      {reasoning && <ReasoningBlock reasoning={reasoning} />}
      {footnote && (
        <div style={{ marginTop:8, fontSize:10, color:C.dim, fontStyle:'italic', lineHeight:1.5 }}>
          {footnote}
        </div>
      )}
    </div>
  );
}

// ─── Reasoning block — three labeled lines per chart ─────
// What it shows / How to read it / Why it matters. Quietly
// authoritative; sits between the chart and the provenance
// footnote. Researcher-grade explanation, not marketing.
function ReasoningBlock({ reasoning }) {
  // Collapsed by default — the chart itself should communicate
  // most of what's needed; the reasoning is for readers who want
  // the methodology context. Per Tim's polish rule (2026-05-03).
  const [expanded, setExpanded] = useState(false);

  const rows = [
    { label: 'What it shows',  color: C.teal,   text: reasoning.shows  },
    { label: 'How to read it', color: C.gold,   text: reasoning.read   },
    { label: 'Why it matters', color: C.purple || C.amber, text: reasoning.matters },
  ].filter(r => r.text);
  if (rows.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          background: 'transparent', border: 'none',
          padding: '4px 0', cursor: 'pointer',
          fontFamily: F.mono, fontSize: 9, letterSpacing: '0.08em',
          color: expanded ? C.gold : C.dim,
          textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: 6,
          transition: 'color 0.12s',
        }}
        onMouseEnter={(e) => { if (!expanded) e.currentTarget.style.color = C.mid; }}
        onMouseLeave={(e) => { if (!expanded) e.currentTarget.style.color = C.dim; }}
      >
        <span style={{
          display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.12s',
        }}>▸</span>
        How to read this
      </button>
      {expanded && (
        <div style={{
          marginTop: 8, padding: '12px 14px',
          background: C.s2, border: `1px solid ${C.b2}`, borderRadius: 6,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: r.color, fontWeight: 500, flexShrink: 0, minWidth: 108, paddingTop: 1,
              }}>
                ◆ {r.label}
              </div>
              <div style={{
                fontSize: 11, color: C.mid, lineHeight: 1.6, flex: 1, fontFamily: F.sans,
              }}>
                {r.text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PROVENANCE FOOTER (shared with donor brief style)
// ═══════════════════════════════════════════════════════════
function Provenance({ run, comparisons }) {
  return (
    <div style={{
      marginTop:20, padding:'14px 18px',
      background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6,
      fontSize:10, color:C.dim, lineHeight:1.6, fontFamily:F.mono,
    }}>
      <div style={{
        fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase',
        color:C.gold, marginBottom:8, fontWeight:500,
      }}>
        Provenance — what these numbers actually are
      </div>
      <div style={{ marginBottom:6 }}>
        Findings derived from run <span style={{ color:C.text }}>{run?.id}</span> on{' '}
        {run?.completed_at ? new Date(run.completed_at).toLocaleString() : '—'} ·{' '}
        {comparisons?.length || 0} comparisons evaluated · methodology versions stored per row.
      </div>
      <div style={{ marginBottom:6 }}>
        Distributions are histogram bins computed at simulation time. Medians + IQR persisted alongside means since v1.0.1 (heavy-tail honest).
      </div>
      <div>
        Watch List Trajectories use the peak/trough/current from each system&apos;s MVP/Recovery dynamic events (where available) — not the full time series. Full-series sparklines arrive in v1.1 once the loaders preserve all years.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PNG EXPORT — SVG to canvas to PNG, no library deps
// ═══════════════════════════════════════════════════════════
function downloadSvgAsPng(svgEl, filename, scale = 2) {
  if (!svgEl || typeof window === 'undefined') return;
  try {
    const xml = new XMLSerializer().serializeToString(svgEl);
    const svg64 = window.btoa(unescape(encodeURIComponent(xml)));
    const dataUri = 'data:image/svg+xml;base64,' + svg64;
    const img = new Image();
    img.onload = () => {
      const w = svgEl.clientWidth || 800;
      const h = svgEl.clientHeight || 400;
      const canvas = document.createElement('canvas');
      canvas.width  = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      // Dark background so the PNG matches the on-screen aesthetic
      ctx.fillStyle = '#070708';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    };
    img.src = dataUri;
  } catch (err) {
    console.error('[downloadSvgAsPng]', err);
  }
}
